/**
 * 作り直しの案件の操作（API §3.6・§4.0）— 確定と承認の入出力契約。
 *
 * 旧表を相手にしていた確定と承認（撤去済み）との差は 3 つである。
 *
 * | 項目 | 旧 | 作り直し |
 * |---|---|---|
 * | 案件番号 | 文字列（`AIRCLOSET-139162`） | number（`139162`。画面が前置きを付けて 6 桁で出す） |
 * | 確定が止める理由 | 未承認と参照の残る用語の削除 | それに**土台ずれ**が加わる |
 * | 承認が指す相手 | 変更行の id（`changeIds[]`） | その案件の変更の行（`nodeIds[]`） |
 *
 * 意味論は共通部（`@cortex/domain-spec-graph`）の確定と承認が持ち、ここは型の写しだけで判定も
 * 合成もしない。`orpc-contract.ts` へは設計の名前（`merge` / `approveTermChanges`）で載せている。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの案件の確定と承認の入出力契約（案件番号は number、確定は土台ずれで止まる）
 * @graph-connects none
 */
import { ELEMENT_APPROVAL_DECISIONS, ELEMENT_MERGE_BLOCKER_KINDS, ELEMENT_MERGE_OUTCOMES, REJECTION_REASONS, } from '@cortex/domain-spec-graph';
import { z } from 'zod';
import { NonEmptyStringSchema } from '../spec-graph-common.js';
/**
 * 作り直しの案件番号。**1 以上の整数**である（決定 2）。
 *
 * `AIRCLOSET-<5 桁以上の数字>` の形では受けない。前置きは画面が付けるもので、値そのものは
 * 番号だからである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの案件番号の入力スキーマ（1 以上の整数）
 * @graph-connects none
 */
export const PjNumberSchema = z.number().int().min(1);
/**
 * 作り直しの要素の id。案件番号と同じく 1 以上の整数である。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの要素の id の入力スキーマ（1 以上の整数）
 * @graph-connects none
 */
export const NodeIdSchema = z.number().int().min(1);
/**
 * 確定の結末。共通部の 5 値を写す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 確定の結末 5 値の出力スキーマ
 * @graph-connects none
 */
export const MergeOutcomeSchema = z.enum(ELEMENT_MERGE_OUTCOMES);
/**
 * 確定を止める理由 1 件。**どのノードで止まったかを必ず持つ。**
 *
 * 直し方が「その案件の番号で、該当のノードを今の master ＋ 案件の差分で書き直す」なので、
 * ノードの一覧が無いと画面が直す先を示せない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 確定を止める理由 1 件の出力スキーマ（種別・対象のノード・説明）
 * @graph-connects none
 */
export const MergeBlockerSchema = z.object({
    kind: z.enum(ELEMENT_MERGE_BLOCKER_KINDS),
    nodeIds: z.array(NodeIdSchema),
    message: NonEmptyStringSchema,
});
/**
 * 確定の入力。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 確定の入力スキーマ（案件番号と試し実行の別）
 * @graph-connects none
 */
export const MergePjInputSchema = z.object({
    pjNumber: PjNumberSchema,
    /** true なら 1 行も書かず、入る予定のノードと止める理由だけを返す。 */
    dryRun: z.boolean().default(false),
});
/**
 * 確定の結果。
 *
 * 件数ではなく**ノードの id** を返す（決定 7。`appliedChangeIds[]` → `appliedNodeIds[]`）。
 * 案件の変更が（案件, ノード）で 1 行になったので、入ったものはノードで数えるのが正しい。
 *
 * `blockers` は常に配列で返す。任意にすると「未指定」と「0 件」が同じ形になり、画面が
 * 止まった理由を `outcome` と `blockers` の 2 箇所で判定できてしまう。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 確定の結果の出力スキーマ（結末・入れたノード・止めた理由）
 * @graph-connects none
 */
export const MergePjOutputSchema = z.object({
    pjNumber: PjNumberSchema,
    outcome: MergeOutcomeSchema,
    dryRun: z.boolean(),
    appliedNodeIds: z.array(NodeIdSchema),
    blockers: z.array(MergeBlockerSchema),
});
/**
 * 承認の判断。共通部の 2 値を写す。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認の判断 2 値の入出力スキーマ
 * @graph-connects none
 */
export const ApprovalDecisionSchema = z.enum(ELEMENT_APPROVAL_DECISIONS);
/**
 * 1 回の呼び出しで判断できるノードの上限。
 *
 * 承認はノードごとに判断する操作で、案件の承認対象が
 * 数百件になる場面は無い（用語の初期登録が約 87 語、ステージは 10〜30 件、
 * アクターは 5〜10 件）。上限が無いと画面の不具合 1 つで際限なく行を書き替えられる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 1 回の承認で判断できるノードの上限
 * @graph-connects none
 */
export const APPROVAL_BATCH_LIMIT = 200;
/**
 * 承認の入力。**指す相手が変更行の id からノードの id へ替わる。**
 *
 * 却下には理由を必須にする。却下は提案者へ「なぜ通らないか」を返す唯一の経路で、理由の無い
 * 却下は提案者に直しようが無い。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認の入力スキーマ（案件番号・判断するノード・判断・却下の理由）
 * @graph-connects none
 */
export const ApproveTermChangesInputSchema = z
    .object({
    pjNumber: PjNumberSchema,
    nodeIds: z.array(NodeIdSchema).min(1).max(APPROVAL_BATCH_LIMIT),
    decision: ApprovalDecisionSchema,
    reason: z.string().trim().nullable().default(null),
})
    .refine((input) => input.decision !== 'rejected' || (input.reason ?? '') !== '', {
    message: '却下には理由が必要です',
    path: ['reason'],
});
/**
 * 共通部が判断を拒否した理由 1 件。`Rejection` の写し（`kind` は固定なので運ばない）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 共通部が操作を拒否した理由 1 件の出力スキーマ
 * @graph-connects none
 */
export const ApprovalRejectionSchema = z.object({
    reason: z.enum(REJECTION_REASONS),
    targetId: NonEmptyStringSchema.nullable(),
    message: z.string(),
    path: z.array(z.string()),
});
/**
 * 承認の結果。
 *
 * 拒否を HTTP のエラーではなく `outcome` で返すのは確定と同じ理由である — 「例外の有無」と
 * 「`outcome` の値」の 2 つを見る実装を生まないため。`rejected` のときは 1 行も書いていない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認の結果の出力スキーマ（判断したノード、または拒否の一覧）
 * @graph-connects none
 */
export const ApproveTermChangesOutputSchema = z.discriminatedUnion('outcome', [
    z.object({
        outcome: z.literal('applied'),
        pjNumber: PjNumberSchema,
        decidedNodeIds: z.array(NodeIdSchema),
    }),
    z.object({
        outcome: z.literal('rejected'),
        pjNumber: PjNumberSchema,
        rejections: z.array(ApprovalRejectionSchema),
    }),
]);
/**
 * 用語の備考を書く入力。`note` が null なら備考を消す。
 *
 * 形の検査（空白だけの本文・用語の番号）は共通部（`writeSpecTermNote`）が持つ。ここで同じ
 * 判定を複製すると、共通部の規則が変わったときに画面の入口だけが黙ってずれる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の備考を画面から書く入力スキーマ（案件番号・要求・用語の番号・備考）
 * @graph-connects none
 */
export const UpdateTermNoteInputSchema = z.object({
    pjNumber: PjNumberSchema,
    requirementId: z.number().int(),
    termId: z.number().int(),
    note: z.string().nullable(),
});
/**
 * 用語の備考を書いた結果。`rejected` のときは 1 行も書いていない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の備考を書いた結果の出力スキーマ（書いた用語、または拒否の一覧）
 * @graph-connects none
 */
export const UpdateTermNoteOutputSchema = z.discriminatedUnion('outcome', [
    z.object({
        outcome: z.literal('applied'),
        pjNumber: PjNumberSchema,
        termId: z.number().int(),
    }),
    z.object({
        outcome: z.literal('rejected'),
        pjNumber: PjNumberSchema,
        rejections: z.array(z.object({ reason: z.string(), pointer: z.string(), message: z.string() })),
    }),
]);
//# sourceMappingURL=pj-operations.js.map